import { motion } from 'framer-motion';
import { Wrench, Replace, Cable, PlugZap, HardHat, Gauge, ArrowUpRight } from 'lucide-react';
import { KRAFT } from '@/constants/testIds';
import { scrollToId } from '@/components/SmoothScroll';

const SERVICES = [
  {
    id: 'naprawy',
    num: '01',
    icon: Wrench,
    title: 'Naprawy elektryczne',
    body:
      'Zwarcia, brak zasilania, uszkodzone obwody, wybijające bezpieczniki, iskrzące gniazda — diagnoza z pomiarami i przywrócenie sprawności całości instalacji.',
    tags: ['Diagnostyka', 'Pomiary', 'Serwis'],
  },
  {
    id: 'wymiana',
    num: '02',
    icon: Replace,
    title: 'Wymiana bezpieczników, żarówek, gniazd, wyłączników',
    body:
      'Szybka wymiana elementów, które się zużyły lub uległy uszkodzeniu. Dobór osprzętu do parametrów instalacji, tak aby zabezpieczenia realnie chroniły obwód.',
    tags: ['Bezpieczniki', 'Osprzęt', 'RCD'],
  },
  {
    id: 'modernizacja',
    num: '03',
    icon: Cable,
    title: 'Modernizacja i rozbudowa instalacji',
    body:
      'Dostosowanie instalacji do rzeczywistego obciążenia — nowa kuchnia, klimatyzacja, indukcja, ładowarka EV, pompa ciepła. Rozbudowa rozdzielnicy i doprowadzenie nowych obwodów.',
    tags: ['Rozdzielnica', 'Nowe obwody', 'EV / AGD'],
  },
  {
    id: 'montaz',
    num: '04',
    icon: PlugZap,
    title: 'Montaż i wymiana osprzętu',
    body:
      'Gniazda, oprawy oświetleniowe, włączniki, ściemniacze, czujniki ruchu — dobór estetyczny i techniczny, montaż zgodny z DTR producenta i normami PN-IEC.',
    tags: ['Gniazda', 'Oświetlenie', 'Włączniki'],
  },
  {
    id: 'nowe-domy',
    num: '05',
    icon: HardHat,
    title: 'Instalacje w nowych domach i remontach',
    body:
      'Kompleksowe wykonanie instalacji elektrycznej — od projektu przez rozprowadzenie po odbiór z protokołami pomiarowymi. Współpracujemy z generalnym wykonawcą i inwestorem.',
    tags: ['Nowa budowa', 'Remont', 'Odbiór z protokołem'],
  },
  {
    id: 'pomiary',
    num: '06',
    icon: Gauge,
    title: 'Pomiary okresowe i badania urządzeń',
    body:
      'Pomiary skuteczności ochrony przeciwporażeniowej, rezystancji izolacji, wyłączników RCD, uziemień i ciągłości PE — wraz z protokołem akceptowanym przez ubezpieczyciela i inspektorat.',
    tags: ['PN-HD 60364-6', 'Protokół', 'Odbiory'],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid={KRAFT.servicesRoot}
      className="relative bg-ink-900 py-24 md:py-32 border-y border-white/10 overflow-hidden"
    >
      {/* subtle radial glow */}
      <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-volt/[0.04] blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-volt/[0.03] blur-3xl pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
            >
              <div className="w-8 h-px bg-volt" />
              <span className="eyebrow">Zakres usług · Oferta</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95] max-w-4xl"
            >
              Od wymiany żarówki po <br className="hidden md:block" />
              <span className="text-volt italic font-normal">kompletną instalację</span>.
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-md text-slate-300 leading-relaxed"
          >
            Każda usługa realizowana przez elektryka z aktualnymi uprawnieniami SEP i kończy się
            protokołem pomiarowym. Bez cichych „napraw na chwilę".
          </motion.p>
        </div>

        {/* Service cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                data-testid={KRAFT.serviceCard(s.id)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="relative bg-ink-900 p-8 md:p-10 group hover:bg-ink-950 transition-colors duration-500 overflow-hidden"
              >
                {/* Big background number */}
                <div
                  aria-hidden
                  className="absolute right-4 top-2 font-display text-[130px] leading-none text-white/[0.04] group-hover:text-volt/[0.08] transition-colors duration-500 select-none"
                >
                  {s.num}
                </div>
                <div className="relative">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 border border-volt/60 bg-ink-950 flex items-center justify-center group-hover:bg-volt group-hover:border-volt transition-colors">
                      <Icon className="w-5 h-5 text-volt group-hover:text-ink-950 transition-colors" strokeWidth={2} />
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      Usługa · {s.num}
                    </div>
                  </div>
                  <h3 className="font-display uppercase text-white text-xl md:text-2xl tracking-tight leading-tight mb-4 min-h-[3.5rem]">
                    {s.title}
                  </h3>
                  <p className="text-slate-300 text-sm md:text-[15px] leading-relaxed">
                    {s.body}
                  </p>
                  <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span
                        key={t}
                        className="px-2 py-1 border border-white/10 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* CTA row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 p-6 md:p-8 border border-white/10 bg-ink-950"
        >
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt mb-2">Nie znalazłeś swojego zakresu?</div>
            <div className="font-display uppercase text-white text-xl md:text-2xl tracking-tight leading-tight max-w-xl">
              Zadzwoń — wyceniamy nietypowe zlecenia i pilne interwencje.
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="tel:+48731842736" className="btn-volt !py-3 !px-5 !text-[11px] whitespace-nowrap">
              Zadzwoń teraz
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <button
              onClick={() => scrollToId('booking')}
              className="btn-outline !py-3 !px-5 !text-[11px] whitespace-nowrap"
            >
              Umów wizytę
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
